/**
 * Cómo se colocan las piezas imprimibles de un producto de varias piezas.
 *
 * Se mueve la malla, no la geometría: es reversible y no toca ni el GLB ni el
 * 3MF. Vive aquí, fuera de React y de Three.js más allá de los vectores, para
 * que lo compartan el visor del editor (`Viewer2.jsx`) y el hook de los visores
 * de página completa (`useModel3DViewer.js`) — la caja de luz se abre igual
 * desde el editor que desde el enlace público.
 *
 *   'assembled' — el producto cerrado, tal y como se usa
 *   'open'      — la tapa levantada sobre la caja, para ver el interior
 *   'separated' — las dos piezas una al lado de la otra, para revisar cada una
 *                 por separado antes de generar el 3MF
 *
 * Con la caja para posavasos lo que se mueve es la pila: 'open' la saca de la
 * caja hacia arriba y 'separated' la deja en el suelo al lado.
 */

import * as THREE from 'three';

// Duración de la transición entre disposiciones de piezas. Lo bastante lenta
// para leer qué pieza se mueve y hacia dónde, lo bastante corta para no estorbar
// al ir alternando entre vistas mientras se revisa el modelo.
export const LAYOUT_TWEEN_MS = 700;

// Arranca y frena suave: la tapa despega y se posa en lugar de aparecer a
// velocidad constante, que es lo que hace legible el movimiento.
export const easeInOutCubic = (t) => (
    t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2
);

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** La caja para posavasos: por convención del backend, las mallas `holder_*`. */
export const isHolderPart = (part) => String(part?.name || '').startsWith('holder_');

/** Las paredes del servilletero de paredes: `wall_a__*` y `wall_b__*`. */
export const isNapkinWallPart = (part) => /^wall_[ab]__/.test(String(part?.name || ''));

/**
 * Las piezas que se apartan: por convención del backend, las `front_*`; si
 * hay caja para posavasos, todo lo que no es caja, que es la pila; y en el
 * servilletero de paredes, las paredes (la base se queda).
 */
export const isMovablePart = (part, parts = []) => (
    String(part?.name || '').startsWith('front_')
    || isNapkinWallPart(part)
    || (!isHolderPart(part) && parts.some(isHolderPart))
);

/** ¿Tiene este modelo piezas separables, o es un sólido único? */
export const hasSeparableParts = (parts = []) => parts.some((part) => isMovablePart(part, parts));

/**
 * Dibuja el resto de la pila de posavasos dentro de la caja.
 *
 * El GLB trae un solo posavasos y, en los extras de la escena, cuántos van y
 * cada cuánto (`holder_stack`, en mm). Las copias se cuelgan como hijas de
 * cada malla del posavasos, así que heredan sin más la posición de las vistas
 * abierta/separada, la visibilidad del panel de piezas y el material (lo
 * comparten), y no aparecen como piezas propias. Hay que llamarla después de
 * recolectar las piezas y antes de medir y apoyar el modelo en el suelo.
 *
 * Devuelve cuántas copias ha añadido.
 */
export function addCoasterStack(object, parts = []) {
    const stack = object?.userData?.holder_stack;
    const count = Math.round(Number(stack?.count) || 0);
    const pitch = (Number(stack?.pitch_mm) || 0) / 10;  // GLB: 1 unidad = 1 cm
    if (count < 2 || pitch <= 0) return 0;

    let added = 0;
    parts
        .filter((part) => part.mesh && !isHolderPart(part))
        .forEach(({ mesh }) => {
            for (let level = 1; level < count; level += 1) {
                // Malla nueva y no `mesh.clone()`: clonar copiaría también las
                // copias ya colgadas y la pila crecería en exponencial.
                const copy = new THREE.Mesh(mesh.geometry, mesh.material);
                copy.name = `${mesh.name}__pila_${level}`;
                copy.castShadow = mesh.castShadow;
                copy.receiveShadow = mesh.receiveShadow;
                copy.position.set(0, pitch * level, 0);
                copy.userData = { stackCopy: true };
                mesh.add(copy);
                added += 1;
            }
        });
    return added;
}

/**
 * Dónde debe acabar cada malla para una disposición dada.
 *
 * El GLB usa 1 unidad = 1 cm (`_MM_TO_GLB_UNITS` en el runner), de ahí el /10
 * para recibir las distancias en milímetros.
 */
export function computeLayoutTargets(parts = [], layout, separationMm, { modelWidthUnits = 0, frontDrop = 0 } = {}) {
    const gapUnits = (Number(separationMm) || 0) / 10;

    return parts.flatMap((part) => {
        if (!part?.mesh || !part.basePosition) return [];
        const { x, y, z } = part.basePosition;

        let target;
        if (!isMovablePart(part, parts) || layout === 'assembled') {
            target = new THREE.Vector3(x, y, z);
        } else if (layout === 'open') {
            target = new THREE.Vector3(x, y + gapUnits, z);
        } else {
            // 'separated': se aparta en X y se baja al suelo, de modo que las
            // dos piezas quedan apoyadas sobre el mismo plano.
            target = new THREE.Vector3(x + modelWidthUnits + gapUnits, y - frontDrop, z);
        }
        return [{ mesh: part.mesh, target }];
    });
}

/**
 * Prepara la transición hacia unos destinos. Devuelve null cuando no hay nada
 * que mover: las piezas que ya están en su sitio (la caja trasera, siempre) no
 * entran en la interpolación.
 */
export function createLayoutTween(targets = []) {
    const entries = targets
        .map(({ mesh, target }) => ({ mesh, from: mesh.position.clone(), target }))
        .filter(({ from, target }) => from.distanceToSquared(target) > 1e-9);

    if (!entries.length) return null;
    return { entries, startedAt: now(), duration: LAYOUT_TWEEN_MS };
}

/** Coloca las piezas en seco, sin animar. */
export function applyLayoutTargets(targets = []) {
    targets.forEach(({ mesh, target }) => mesh.position.copy(target));
}

/**
 * Avanza una transición un frame. Devuelve true mientras siga viva, de modo que
 * el bucle de render pueda descartarla cuando termine.
 */
export function stepLayoutTween(tween) {
    if (!tween) return false;

    const progress = Math.min((now() - tween.startedAt) / tween.duration, 1);
    const eased = easeInOutCubic(progress);
    tween.entries.forEach(({ mesh, from, target }) => {
        mesh.position.lerpVectors(from, target, eased);
    });
    return progress < 1;
}

/**
 * Medidas que necesitan las disposiciones, tomadas una sola vez con el modelo
 * ensamblado para que separar y volver a juntar no acumule desplazamientos.
 */
export function measureAssembly(object, parts = []) {
    const size = new THREE.Vector3();
    let frontDrop = 0;
    try {
        const assembledBox = new THREE.Box3().setFromObject(object);
        assembledBox.getSize(size);

        const frontMeshes = parts
            .filter((part) => isMovablePart(part, parts))
            .map((part) => part.mesh)
            .filter(Boolean);
        if (frontMeshes.length) {
            const frontBox = new THREE.Box3();
            frontMeshes.forEach((mesh) => frontBox.expandByObject(mesh));
            // Distancia entre la base de la pieza frontal y la del conjunto: es
            // lo que hay que bajarla para dejarla apoyada junto a la trasera.
            frontDrop = Math.max(0, frontBox.min.y - assembledBox.min.y);
        }
    } catch {
        // Un modelo sin geometría medible se queda con las medidas a cero: las
        // disposiciones quedan inertes, que es mejor que no cargar el visor.
        frontDrop = 0;
    }
    return { size, frontDrop };
}
